import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid2";

import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import ArrowDownward from "@mui/icons-material/ArrowDownward";

import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

import AcreedoresApi from "api/acreedoresApi";

const EditAcreedorModal = ({ open, onClose, acreedor, onSuccess }) => {
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    tipo_identificacion: "",
    identificacion: "",
    tipo: "",
    nombre: "",
    apellido: "",
    direccion: "",
    ciudad: "",
    numero_contacto: "",
    es_recurrente: false,
    correos: [],
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (acreedor) {
      setFormData({
        ...acreedor,
        correos: acreedor.correos || [],
      });
    }
  }, [acreedor]);

  const handleIdentificacionBlur = async () => {
    const identificacion = formData.identificacion;
    if (identificacion.length < 5) {
      setErrors((prev) => ({
        ...prev,
        identificacion: "Debe tener al menos 5 caracteres",
      }));
      return;
    }
    try {
      //if (!acreedor) {}
      const response = await AcreedoresApi.fetchAcreedorByIdentificacion(identificacion);
      const consAcreedor = response.data;
      if (consAcreedor) {
        setErrors((prev) => ({
          ...prev,
          identificacion: "La identificación ya existe, se pasa a este nuevo acreedor",
        }));
        setFormData({
          ...consAcreedor,
          correos: consAcreedor.correos || [],
        });
      }
    } catch (error) {
      console.error("Error al verificar acreedor:", error);
      setErrors((prev) => ({
        ...prev,
        identificacion: "Error al verificar la identificación",
      }));
    }
  };

  const handleIdentificacionChange = (e) => {
    const identificacion = e.target.value;
    // Aplicar formato según tipo de identificación
    const cleanValue =
      formData.tipo_identificacion === "NIT"
        ? identificacion.replace(/[^\d-]/g, "").slice(0, 20)
        : identificacion.replace(/\D/g, "").slice(0, 20);
    setFormData((prev) => ({ ...prev, identificacion: cleanValue }));
    setErrors((prev) => ({ ...prev, identificacion: "" }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "nombre" || name === "apellido") {
      const cleanValue = value.replace(/[^a-záéíóúñA-ZÁÉÍÓÚÑ\s]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: cleanValue,
      }));
    } else if (name === "tipo") {
      const cleanValue = value.replace(/[^a-záéíóúñA-ZÁÉÍÓÚÑ\s]/g, "").toUpperCase();
      setFormData((prev) => ({
        ...prev,
        [name]: cleanValue,
      }));
    } else if (name === "numero_contacto") {
      const cleanValue = value.replace(/\D/g, "").slice(0, 15); // Solo números, máximo 15 dígitos
      setFormData((prev) => ({
        ...prev,
        [name]: cleanValue,
      }));
    } else if (name === "tipo_identificacion") {
      if (value === "NIT") {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          tipo: "", // Permitir al usuario definir el tipo si es NIT
          apellido: "",
          identificacion: "",
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
          tipo: "PERSONA", // Asigna automáticamente "PERSONA" cuando se selecciona CC
          identificacion: "",
        }));
      }
    } else {
      const newValue = type === "checkbox" ? checked : value;
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  const handleCorreoChange = (index, value) => {
    const newCorreos = [...formData.correos];
    newCorreos[index] = { ...newCorreos[index], email: value };
    setFormData({ ...formData, correos: newCorreos });
  };

  const addCorreo = () => {
    setFormData({
      ...formData,
      correos: [...formData.correos, { email: "", orden: formData.correos.length }],
    });
  };

  const removeCorreo = (index) => {
    const newCorreos = formData.correos.filter((_, i) => i !== index);
    const reorderedCorreos = newCorreos.map((correo, i) => ({
      ...correo,
      orden: i,
    }));
    setFormData({ ...formData, correos: reorderedCorreos });
  };

  const moveCorreo = (index, direction) => {
    if (
      (direction === -1 && index === 0) ||
      (direction === 1 && index === formData.correos.length - 1)
    ) {
      return;
    }
    const newCorreos = [...formData.correos];
    const temp = newCorreos[index];
    newCorreos[index] = newCorreos[index + direction];
    newCorreos[index + direction] = temp;
    const reorderedCorreos = newCorreos.map((correo, i) => ({
      ...correo,
      orden: i,
    }));
    setFormData({ ...formData, correos: reorderedCorreos });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.identificacion) {
      setError("La Identificación es un campo obligatorio.");
      return;
    }
    try {
      const dataToSend = {
        ...formData,
        apellido: formData.tipo_identificacion === "NIT" ? "" : formData.apellido,
        tipo: formData.tipo_identificacion === "CC" ? "PERSONA" : formData.tipo,
        correos_data: formData.correos.map((correo, index) => ({
          email: correo.email,
          orden: index,
        })),
      };
      await AcreedoresApi.updateAcreedor(acreedor.id, dataToSend);
      resetState();
      onSuccess();
    } catch (errors) {
      console.error("Error al guardar acreedor:", errors);
    }
  };

  const resetState = () => {
    setError(undefined);
    setErrors(undefined);
    setFormData({
      tipo_identificacion: "",
      identificacion: "",
      tipo: "",
      nombre: "",
      apellido: "",
      direccion: "",
      ciudad: "",
      es_recurrente: false,
      correos: [],
    });
  };

  const hCClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog open={open} onClose={hCClose} maxWidth="md" fullWidth>
      <DialogTitle>Editar Acreedor</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              {error && (
                <Alert severity="errors" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Tipo de Identificación
              </SuiTypography>
              <Select
                name="tipo_identificacion"
                value={formData.tipo_identificacion}
                onChange={handleChange}
                label="Tipo de Identificación"
              >
                <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                <MenuItem value="NIT">Número de Identificación Tributaria</MenuItem>
                <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                <MenuItem value="PAS">Pasaporte</MenuItem>
              </Select>
            </Grid>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Identificación
              </SuiTypography>
              <SuiInput
                name="identificacion"
                placeholder={
                  formData.tipo_identificacion === "NIT"
                    ? "123456789-1"
                    : formData.tipo_identificacion === "PAS"
                    ? "Alfanumérico 5-20 caracteres"
                    : "Ingrese máximo 20 dígitos numéricos"
                }
                value={formData.identificacion}
                onChange={handleIdentificacionChange}
                onBlur={handleIdentificacionBlur}
                error={Boolean(errors?.identificacion)}
                fullWidth
                disabled={false}
              />
              {errors?.identificacion && (
                <SuiTypography variant="caption" color="error">
                  {errors?.identificacion}
                </SuiTypography>
              )}
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Nombres
              </SuiTypography>
              <SuiInput
                name="nombre"
                placeholder={formData.tipo_identificacion === "NIT" ? "Razón Social" : "Nombre"}
                value={formData.nombre}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            {formData.tipo_identificacion !== "NIT" && (
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Apellidos
                </SuiTypography>
                <SuiInput
                  name="apellido"
                  placeholder="Apellido"
                  value={formData.apellido || ""}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
            )}
            {formData.tipo_identificacion === "NIT" && (
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="light">
                  Tipo
                </SuiTypography>
                <SuiInput
                  name="tipo"
                  placeholder="Tipo"
                  value={formData.tipo || ""}
                  onChange={handleChange}
                  helperText="Solo letras y espacios permitidos (se convertirá a mayúsculas)"
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiTypography variant="h6" fontWeight="light">
                Dirección
              </SuiTypography>
              <SuiInput
                name="direccion"
                placeholder="Dirección"
                value={formData.direccion || ""}
                onChange={handleChange}
                multiline
                rows={2}
                fullWidth
              />
            </Grid>
            <Grid size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                Ciudad
              </SuiTypography>
              <SuiInput
                name="ciudad"
                placeholder="Ciudad"
                value={formData.ciudad || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid size={3}>
              <SuiTypography variant="h6" fontWeight="light">
                No. Contacto
              </SuiTypography>
              <SuiInput
                name="numero_contacto"
                placeholder="Número de Contacto"
                value={formData.numero_contacto || ""}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiTypography variant="h6" fontWeight="light">
                ¿Es Recurrente?{" "}
                <Switch
                  checked={formData.es_recurrente}
                  onChange={handleChange}
                  name="es_recurrente"
                />
              </SuiTypography>
            </Grid>
          </Grid>
          {/* Sección de Correos */}
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={12}>
              <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SuiBox component="h6">Correos Electrónicos</SuiBox>
                <SuiButton variant="gradient" buttonColor="dark" size="small" onClick={addCorreo}>
                  <AddIcon /> Agregar Correo
                </SuiButton>
              </SuiBox>
            </Grid>
          </Grid>
          {formData.correos && (
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                {formData.correos.map((correo, index) => (
                  <SuiBox key={index} display="flex" alignItems="center" mb={2}>
                    <SuiInput
                      value={correo.email}
                      onChange={(e) => handleCorreoChange(index, e.target.value)}
                      placeholder="Correo electrónico"
                      fullWidth
                    />
                    <IconButton onClick={() => moveCorreo(index, -1)} disabled={index === 0}>
                      <ArrowUpward />
                    </IconButton>
                    <IconButton
                      onClick={() => moveCorreo(index, 1)}
                      disabled={index === formData.correos.length - 1}
                    >
                      <ArrowDownward />
                    </IconButton>
                    <IconButton onClick={() => removeCorreo(index)} color="errors">
                      <DeleteIcon />
                    </IconButton>
                  </SuiBox>
                ))}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={hCClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark">
            Actualizar
          </SuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

EditAcreedorModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  acreedor: PropTypes.object,
  onSuccess: PropTypes.func.isRequired,
};

export default EditAcreedorModal;
